import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { ModalContent, Card, Common, Icon } from "@/components";
import { manifest, route } from "../models";
import { useGetManifestDetailsByRoutes } from "../services/deliveryManifest";
import { TIME_FORMAT } from "@/configuration/globalVariable";
import moment from "moment";
import { addManifests } from "../providers/deliveryManifest/slice";
import { useAppDispatch } from "@/sharedStore/hooks";

const RouteItem = ({
  isActive,
  data,
  disable,
  onClick,
}: {
  isActive?: boolean;
  disable?: boolean;
  data: route;
  onClick: () => void;
}) => {
  return (
    <div
      className={clsx(
        "text-sm flex items-center mb-0.5 p-3",
        "hover:bg-neutral-10 cursor-pointer",
        isActive && "bg-neutral-10",
        disable && "bg-neutral-5"
      )}
      onClick={onClick}
    >
      <div>
        <Common.DotColor style={{ backgroundColor: data.colour }} />
      </div>
      <span className="font-semibold mx-1.5 line-clamp-1">{data.name}</span>
      {isActive && (
        <div className="ml-auto">
          <Icon.Checked size="14" className="fill-blue" />
        </div>
      )}
    </div>
  );
};

interface Props {
  open: boolean;
  routeList: route[];
  manifests: manifest[];
  date: Date;
  // onAddEmptyRoute: (route: route[]) => void;
  onClose: () => void;
}

export const ModalAddEmptyRouteScreen = ({
  open,
  manifests,
  routeList,
  date,
  // onAddEmptyRoute,
  onClose,
}: Props) => {
  const dispatch = useAppDispatch();
  const [routesSelected, setRoutesSelected] = useState<route[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");

  const getManifestDetailsByRoutes = useGetManifestDetailsByRoutes();

  const {
    data: getManifestDetailsByRoutesData,
    isSuccess: getManifestDetailsByRoutesIsSuccess,
    isLoading: getManifestDetailsByRoutesIsLoading,
  } = getManifestDetailsByRoutes;

  const results = useMemo<route[]>(() => {
    return routeList.filter((item: route) => {
      if (
        item.name &&
        item.name.toUpperCase().indexOf(searchKeyword.toUpperCase()) > -1 &&
        !manifests.some((manifest) => manifest.route.id === item.id)
      )
        return true;
      return false;
    });
  }, [routeList, searchKeyword, manifests]);

  useEffect(() => {
    if (
      getManifestDetailsByRoutesIsSuccess &&
      getManifestDetailsByRoutesData.length > 0
    ) {
      dispatch(addManifests({ manifests: getManifestDetailsByRoutesData }));
      handleClose();
    }
    // eslint-disable-next-line
  }, [getManifestDetailsByRoutesIsSuccess]);

  const handleSelectRoute = (route: route) => {
    if (routesSelected.includes(route)) {
      setRoutesSelected(routesSelected.filter((r) => r.id !== route.id));
    } else {
      setRoutesSelected([...routesSelected, route]);
    }
  };

  const handleAddRoutes = () => {
    getManifestDetailsByRoutes.mutate({
      date: moment(date).format(TIME_FORMAT.REQUEST),
      routeIds: routesSelected.map((r) => r.id),
    });
  };

  const handleClickAllRoutes = () => {
    if (routesSelected.length === routeList.length) {
      setRoutesSelected([]);
    } else {
      setRoutesSelected(routeList);
    }
  };

  const handleClose = () => {
    setRoutesSelected([]);
    onClose();
  };

  return (
    <ModalContent
      open={open}
      onClose={handleClose}
      panelClassWidth="max-w-[31.25rem] w-full"
    >
      <Card title={`Add routes`}>
        <div className="p-4">
          <Common.Input
            iconRight={Icon.Search}
            iconType="stroke"
            placeholder="Search route name"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onClear={() => setSearchKeyword("")}
          />
          <div className="flex items-center justify-between mt-3">
            <div className="text-neutral-60">Routes</div>
            <Common.Button color="transparent" onClick={handleClickAllRoutes}>
              {routesSelected.length === routeList.length
                ? "Deselect all"
                : "Select all"}
            </Common.Button>
          </div>
          <div className="h-52 overflow-y-auto">
            {routeList && routeList.length ? (
              <>
                {results.length ? (
                  results.map((route) => {
                    return (
                      <RouteItem
                        key={route.id}
                        data={route}
                        onClick={() => handleSelectRoute(route)}
                        isActive={routesSelected.includes(route)}
                      />
                    );
                  })
                ) : (
                  <Common.NoData title={"No Route founded"} />
                )}
              </>
            ) : (
              <Common.NoData title={"No Route data!"} />
            )}
          </div>
        </div>
        <div className="shadow-overline px-4 py-3 flex space-x-2 justify-end">
          {routesSelected.length > 0 && (
            <span className="text-neutral-60 self-center mr-auto">
              {routesSelected.length} route
              {routesSelected.length > 1 ? "s" : ""} selected
            </span>
          )}
          <Common.Button onClick={handleClose} color="transparent">
            Cancel
          </Common.Button>
          <Common.Button
            disabled={
              Boolean(!routesSelected.length) ||
              getManifestDetailsByRoutesIsLoading
            }
            isLoading={getManifestDetailsByRoutesIsLoading}
            onClick={handleAddRoutes}
          >
            Add
          </Common.Button>
        </div>
      </Card>
    </ModalContent>
  );
};
